import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { Search, Book, ThumbsUp, ThumbsDown, X, ExternalLink, ChevronRight } from 'lucide-react';
import { Modal } from './Modal';

interface HelpArticle {
  id: string;
  title: string;
  slug: string;
  content: string;
  category: string;
  role: string;
  view_count: number;
  helpful_count: number;
  unhelpful_count: number;
}

interface HelpCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

export const HelpCenter: React.FC<HelpCenterProps> = ({ isOpen, onClose }) => {
  const { profile } = useAuth();
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [filteredArticles, setFilteredArticles] = useState<HelpArticle[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<HelpArticle | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    if (isOpen) {
      loadArticles();
    }
  }, [isOpen, profile?.role]);

  useEffect(() => {
    filterArticles();
  }, [searchQuery, selectedCategory, articles]);

  const loadArticles = async () => {
    try {
      setLoading(true);
      let query = supabase
        .from('help_articles')
        .select('*')
        .eq('is_published', true);

      if (profile?.role) {
        query = query.in('role', [profile.role, 'both']);
      }

      const { data, error } = await query.order('category');

      if (error) throw error;
      setArticles(data || []);
    } catch (error) {
      console.error('Error loading help articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterArticles = () => {
    let filtered = articles;

    if (selectedCategory !== 'all') {
      filtered = filtered.filter(article => article.category === selectedCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(article =>
        article.title.toLowerCase().includes(query) ||
        article.content.toLowerCase().includes(query) ||
        article.category.toLowerCase().includes(query)
      );
    }

    setFilteredArticles(filtered);
  };

  const handleArticleClick = async (article: HelpArticle) => {
    setSelectedArticle(article);

    try {
      await supabase
        .from('help_articles')
        .update({ view_count: article.view_count + 1 })
        .eq('id', article.id);
    } catch (error) {
      console.error('Error updating view count:', error);
    }
  };

  const handleFeedback = async (isHelpful: boolean) => {
    if (!selectedArticle || !profile) return;

    try {
      const { error } = await supabase
        .from('help_article_feedback')
        .upsert({
          article_id: selectedArticle.id,
          user_id: profile.id,
          is_helpful: isHelpful,
        });

      if (error) throw error;

      const increment = isHelpful ? { helpful_count: selectedArticle.helpful_count + 1 } : { unhelpful_count: selectedArticle.unhelpful_count + 1 };

      await supabase
        .from('help_articles')
        .update(increment)
        .eq('id', selectedArticle.id);

      setSelectedArticle({ ...selectedArticle, ...increment });
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  const categories = Array.from(new Set(articles.map(a => a.category)));

  const renderMarkdown = (content: string) => {
    return content.split('\n').map((line, i) => {
      if (line.startsWith('# ')) {
        return <h1 key={i} className="text-2xl font-bold text-slate-900 mt-6 mb-3">{line.slice(2)}</h1>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={i} className="text-xl font-bold text-slate-900 mt-5 mb-2">{line.slice(3)}</h2>;
      }
      if (line.startsWith('- ')) {
        return <li key={i} className="text-slate-700 ml-4 mb-1">{line.slice(2)}</li>;
      }
      if (line.trim() === '') {
        return <br key={i} />;
      }
      return <p key={i} className="text-slate-700 mb-2">{line}</p>;
    });
  };

  if (selectedArticle) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title={selectedArticle.title} size="xl">
        <div className="space-y-6">
          <div className="flex items-center justify-between text-sm text-slate-500">
            <span className="px-2.5 py-1 bg-slate-100 text-slate-700 rounded-full text-xs font-medium">
              {selectedArticle.category}
            </span>
            <button
              onClick={() => setSelectedArticle(null)}
              className="text-slate-600 hover:text-slate-900 flex items-center space-x-1"
            >
              <span>Back to articles</span>
            </button>
          </div>

          <div className="prose max-w-none">
            {renderMarkdown(selectedArticle.content)}
          </div>

          {profile && (
            <div className="pt-6 border-t border-slate-200">
              <p className="text-sm font-medium text-slate-900 mb-3">Was this article helpful?</p>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => handleFeedback(true)}
                  className="flex items-center space-x-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <ThumbsUp className="w-4 h-4 text-slate-600" />
                  <span className="text-sm text-slate-700">Yes</span>
                </button>
                <button
                  onClick={() => handleFeedback(false)}
                  className="flex items-center space-x-2 px-4 py-2 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <ThumbsDown className="w-4 h-4 text-slate-600" />
                  <span className="text-sm text-slate-700">No</span>
                </button>
              </div>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Help Center" size="xl">
      <div className="space-y-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search help articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent"
          />
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              selectedCategory === 'all'
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            All Articles
          </button>
          {categories.map(category => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedCategory === category
                  ? 'bg-slate-900 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {category}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
          </div>
        ) : filteredArticles.length === 0 ? (
          <div className="text-center py-12">
            <Book className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-600">No articles found</p>
          </div>
        ) : (
          <div className="space-y-2">
            {filteredArticles.map(article => (
              <button
                key={article.id}
                onClick={() => handleArticleClick(article)}
                className="w-full text-left p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-slate-700">
                      {article.title}
                    </h3>
                    <p className="text-sm text-slate-600">{article.category}</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 flex-shrink-0 ml-2" />
                </div>
              </button>
            ))}
          </div>
        )}

        <div className="pt-6 border-t border-slate-200">
          <p className="text-sm text-slate-600 text-center">
            Can't find what you're looking for?{' '}
            <a href="mailto:support@vendorboom.com" className="text-slate-900 font-medium hover:underline">
              Contact Support
            </a>
          </p>
        </div>
      </div>
    </Modal>
  );
};
